/**
 * @group Classes
 * 
 * @summary
 * <blockquote>Fetch API-based implementation of the ApiClient interface with environment-aware service configuration.</blockquote>
 * 
 * @example
 * ```typescript
 * import { FetchApiClient } from '@shared/httpService';
 * 
 * // Default behavior - automatically uses LOCAL_API_URL
 * const apiClient = new FetchApiClient(); // No config needed!
 * 
 * // Using service configuration (recommended)
 * const apiClient = new FetchApiClient({
 *   service: { isLocal: true }, // Will use LOCAL_API_URL from environment
 *   timeout: 5000
 * });
 * 
 * // Using direct baseURL (fallback)
 * const apiClient = new FetchApiClient({
 *   baseURL: 'https://api.example.com',
 *   timeout: 5000
 * });
 * 
 * const users = await apiClient.get('/users');
 * ```
 */
import type { ApiClient, FetchApiClientConfig } from '../interfaces';
import type { ApiRequest, ApiResponse } from '../types';
import { DEFAULT_CONFIG, HTTP_METHODS } from '../constants';
import { buildUrl, buildRequestConfig, processResponse } from '../utils';
import { tryCatchAsync, type AsyncResult, type AppError, getEnvironmentValue, ENVIRONMENT_VALUES, getAppBaseUrl, Result, err, ok } from '@shared/utils';
import { Logger } from '@shared/logger';

/**
 * Fetch API-based API client implementation
 * Compatible with both web and mobile (React Native) environments
 */
export class FetchApiClient implements ApiClient {
  private config: FetchApiClientConfig;

  constructor(config: FetchApiClientConfig = {}) {

    this.config = {
      service: config.service || { isLocal: true }, // Default to local if no service config
      timeout: config.timeout || DEFAULT_CONFIG.TIMEOUT,
      headers: { ...DEFAULT_CONFIG.HEADERS, ...config.headers },
      retry: { ...DEFAULT_CONFIG.RETRY, ...config.retry },
      credentials: config.credentials || DEFAULT_CONFIG.CREDENTIALS,
    };

  }

  /**
   * Resolves the baseURL from service configuration
   */
  private resolveBaseURL(): Result<string, AppError> {

    // Check if service is configured
    if (!this.config.service) {
      return err({ errorMessage: "No service configured. Please provide a service configuration." });
    }

    // Handle local development (isLocal is true or undefined with no service)
    if (this.config.service.isLocal === true || (this.config.service.isLocal === undefined && !this.config.service.service)) {
      const result = getAppBaseUrl();

      if (result.isErr) {
        return err({ errorMessage: `API base URL not set: ${result.error.errorMessage}` });
      }

      return ok(result.value);
    }

    // Handle external service (isLocal is false or undefined with service provided)
    if (this.config.service.service) {
      const baseUrlResult = this.config.service.service.getBaseUrl();
      if (baseUrlResult.isErr) {
        return err({ 
          errorMessage: `Failed to get base URL from service: ${baseUrlResult.getFail().errorMessage}` 
        });
      }
      return ok(baseUrlResult.value);
    }

    return err({ errorMessage: "No service configured. Please provide a service configuration." });
  }

  /**
   * Gets headers from service configuration
   */
  private getServiceHeaders(): Record<string, string> {
    if (!this.config.service || this.config.service.isLocal === true || !this.config.service.service) {
      return {};
    }

    const headersResult = this.config.service.service.getHeaders();
    if (headersResult.isErr) {
      // Log error but don't fail - use empty headers as fallback
      return {};
    }
    
    return headersResult.value;
  }

  /**
   * Makes an API request using the fetch API
   */
  async request<T = any>(config: ApiRequest): AsyncResult<ApiResponse<T>, AppError> {
    const resolvedBaseURLResult = this.resolveBaseURL();

    if (resolvedBaseURLResult.isErr) {
      return err(resolvedBaseURLResult.getFail());
    }

    const url = buildUrl(resolvedBaseURLResult.value, config.url, config.params);
    
    // Merge service headers with config headers
    const serviceHeaders = this.getServiceHeaders();
    const mergedHeaders = { ...serviceHeaders, ...this.config.headers };
    
    const requestConfig = buildRequestConfig(config, mergedHeaders, this.config.credentials || 'same-origin');

    // Create internal controller for timeout
    const internalController = new AbortController();
    const timeoutId = setTimeout(() => internalController.abort(), config.timeout || this.config.timeout);

    // Combine external signal (if provided) with internal timeout signal
    const combinedSignal = this.combineAbortSignals([
      internalController.signal,
      ...(config.signal ? [config.signal] : [])
    ]);

    const fetchResult = await tryCatchAsync(async () => {
      const response = await fetch(url, {
        method: config.method,
        headers: requestConfig.headers,
        body: requestConfig.body,
        credentials: requestConfig.credentials,
        signal: combinedSignal,
      });

      return response;
    });

    clearTimeout(timeoutId);

    if (fetchResult.isErr) {
      return err(fetchResult.getFail());
    }

    const response = fetchResult.value;

    // Process the response with responseType from config
    const processResponseResult = await processResponse<T>(response, config.responseType);

    if (processResponseResult.isErr) {
      return err(processResponseResult.getFail());
    }

    return ok(processResponseResult.value);
  }

  /**
   * Combines multiple AbortSignals into one
   * The resulting signal will abort when any of the input signals abort
   */
  private combineAbortSignals(signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();

    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort();
        break;
      }

      signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    return controller.signal;
  }

  /**
   * Makes a GET request
   */
  async get<T = any>(url: string, config?: Partial<ApiRequest>): AsyncResult<ApiResponse<T>, AppError> {
    return this.request<T>({
      url,
      method: HTTP_METHODS.GET,
      ...config,
    });
  }

  /**
   * Makes a POST request
   * 
   * @overload
   * @param url - The endpoint URL
   * @param data - The data to send in the request body
   * @param config - Optional request configuration
   * 
   * @overload
   * @param data - The data to send in the request body (uses empty URL for service-based endpoints)
   * @param config - Optional request configuration
   */
  async post<T = any>(url: string, data?: any, config?: Partial<ApiRequest>): AsyncResult<ApiResponse<T>, AppError>;
  async post<T = any>(data: any, config?: Partial<ApiRequest>): AsyncResult<ApiResponse<T>, AppError>;
  async post<T = any>(urlOrData: string | any, dataOrConfig?: any, config?: Partial<ApiRequest>): AsyncResult<ApiResponse<T>, AppError> {
    // Check if first parameter is a string (URL) or data
    if (typeof urlOrData === 'string') {
      // First overload: post(url, data?, config?)
      return this.request<T>({
        url: urlOrData,
        method: HTTP_METHODS.POST,
        body: dataOrConfig,
        ...config,
      });
    } else {
      // Second overload: post(data, config?)
      return this.request<T>({
        url: '', // Empty URL for service-based endpoints
        method: HTTP_METHODS.POST,
        body: urlOrData,
        ...dataOrConfig,
      });
    }
  }

  /**
   * Makes a PUT request
   */
  async put<T = any>(url: string, data?: any, config?: Partial<ApiRequest>): AsyncResult<ApiResponse<T>, AppError> {
    return this.request<T>({
      url,
      method: HTTP_METHODS.PUT,
      body: data,
      ...config,
    });
  }

  /**
   * Makes a PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: Partial<ApiRequest>): AsyncResult<ApiResponse<T>, AppError> {
    return this.request<T>({
      url,
      method: HTTP_METHODS.PATCH,
      body: data,
      ...config,
    });
  }

  /**
   * Makes a DELETE request
   */
  async delete<T = any>(url: string, config?: Partial<ApiRequest>): AsyncResult<ApiResponse<T>, AppError> {
    return this.request<T>({
      url,
      method: HTTP_METHODS.DELETE,
      ...config,
    });
  }

}
